import { useCallback, useState } from "react";
import { useOnMount } from "../../../../../../hooks/useOnMount.ts";
import { LINE_LAYER_TYPE } from "../../Designer/constants.ts";
import { useLayerRotationEventListener } from "../../Designer/hooks/useLayerRotationEventListener.ts";
import { useLayerSelectionEventListener } from "../../Designer/hooks/useLayerSelectionEventListener.ts";
import { designerCanvasStore } from "../../Designer/stores/designerCanvasStore.ts";
import type { LineLayer, LineLayerSettings } from "../../Designer/types.ts";
import {
  getSpecificActiveLayers,
  normalizeLayerAngle,
} from "../../Designer/utils/layer.ts";
import { updateLineLayer } from "../../Designer/utils/line.ts";
import { DesignerRotationInputNumber } from "../components/DesignerSettings/DesignerRotationInputNumber.tsx";
import { StrokeSettingsSection } from "../components/DesignerSettings/StrokeSettingsSection.tsx";
import { DesignerSettingsItem } from "../components/Layout/DesignerSettingsItem.tsx";
import { DesignerSettingsLayout } from "../components/Layout/DesignerSettingsLayout.tsx";

export const EditLineSettingsControls = () => {
  const { selectedLineLayersSettings, setSelectedLineLayersSettings } =
    useSelectedLineLayersSettings();

  return (
    <DesignerSettingsLayout>
      <DesignerSettingsItem title="Stroke">
        <StrokeSettingsSection
          strokeSettings={{
            strokeColor: selectedLineLayersSettings.stroke,
            strokeStyle: selectedLineLayersSettings.strokeStyle,
            strokeWidth: selectedLineLayersSettings.strokeWidth,
          }}
          onStrokeSettingsChange={(strokeSettings) => {
            const { strokeColor, strokeStyle, strokeWidth } = strokeSettings;
            setSelectedLineLayersSettings({
              stroke: strokeColor ?? selectedLineLayersSettings.stroke,
              strokeStyle:
                strokeStyle ?? selectedLineLayersSettings.strokeStyle,
              strokeWidth:
                strokeWidth ?? selectedLineLayersSettings.strokeWidth,
            });
          }}
          minThickness={0.1}
          maxThickness={40}
        />
      </DesignerSettingsItem>
      <DesignerSettingsItem
        title="Rotation"
        rightItem={
          <DesignerRotationInputNumber
            rotation={selectedLineLayersSettings.rotation}
            onRotationChange={(rotation) =>
              setSelectedLineLayersSettings({ rotation })
            }
          />
        }
      />
    </DesignerSettingsLayout>
  );
};

const useSelectedLineLayersSettings = () => {
  const { fabricCanvas } = designerCanvasStore.useState();

  const [selectedLineLayersSettings, setSelectedLineLayersSettings] = useState<
    Required<LineLayerSettings>
  >({
    stroke: "rgba(0, 0, 0, 1)",
    strokeWidth: 1,
    strokeStyle: "solid",
    rotation: 0,
  });

  const updateSelectedLineLayersSettingsFromLineLayer = useCallback(
    (lineLayer: LineLayer) => {
      setSelectedLineLayersSettings({
        strokeWidth: lineLayer.strokeWidth,
        strokeStyle: lineLayer.strokeStyle,
        rotation: normalizeLayerAngle(lineLayer.angle),
        stroke: lineLayer.stroke,
      });
    },
    [],
  );

  useOnMount(() => {
    if (!fabricCanvas) return;
    const activeLineLayers = getSpecificActiveLayers({
      fabricCanvas,
      layerType: LINE_LAYER_TYPE,
    });
    if (activeLineLayers.length === 0) return;
    updateSelectedLineLayersSettingsFromLineLayer(activeLineLayers[0]);
  });

  useLayerSelectionEventListener({
    fabricCanvas,
    layerType: LINE_LAYER_TYPE,
    onLayerSelectionUpdate: updateSelectedLineLayersSettingsFromLineLayer,
  });

  useLayerRotationEventListener({
    fabricCanvas,
    layerType: LINE_LAYER_TYPE,
    onLayerRotationEvent: (rotation) => {
      setSelectedLineLayersSettings({
        ...selectedLineLayersSettings,
        rotation,
      });
    },
  });

  const updateSelectedLineLayersSettings = useCallback(
    (options: Partial<Required<LineLayerSettings>>) => {
      setSelectedLineLayersSettings({
        ...selectedLineLayersSettings,
        ...options,
      });
      if (!fabricCanvas) return;
      for (const layer of getSpecificActiveLayers({
        fabricCanvas,
        layerType: LINE_LAYER_TYPE,
      })) {
        updateLineLayer(layer, options);
      }
      fabricCanvas.renderAll();
    },
    [fabricCanvas, selectedLineLayersSettings],
  );

  return {
    selectedLineLayersSettings,
    setSelectedLineLayersSettings: updateSelectedLineLayersSettings,
  };
};
